<template>

	<div id="map-wrapper" class="uk-position-relative">
		<!-- <div id="map-wrapper" uk-grid class="uk-grid uk-grid-collapse"> -->
		<div id="results-panel" class="uk-width-1-2@m uk-width-1-3@l uk-padding@m uk-position-relative uk-box-shadow-large">


			<div uk-sticky="media: 1024; offset: 100;">

				<a aria-label="Vis / skjul søk" href="#" uk-toggle="target: #results-panel;  cls: hide"
					class="uk-visible@m panel-toggler uk-box-shadow-large">
					<span>Vis søk</span>
					<span uk-icon="icon: chevron-left; ratio: 1.6"></span>
				</a>

				<h1 class="uk-h4 uk-visible@m uk-text-semibold">Finn strekning</h1>

				<div class="switcher-container uk-position-top-center uk-margin-small-top uk-padding-bottom">
					<ul ref="switcherNav" id="switcher-nav" class="uk-nav">
						<li :class="{ 'uk-active': state.switcherIsActive === true && state.switcherIndex === 0 }">
							<a @click="setActiveSwitcherState(0)" href="#"
								class="uk-button uk-button-primary uk-flex uk-flex-middle uk-text-semibold">
								<span uk-icon="search" class="uk-margin-xsmall-right"></span>
								<span>Søk</span>
							</a>
						</li>
						<li :class="{ 'uk-active': state.switcherIsActive === true && state.switcherIndex === 1 }">
							<a @click="setActiveSwitcherState(1)" href="#"
								class="uk-button uk-button-primary uk-flex uk-flex-middle uk-text-semibold">
								<span uk-icon="filter" class="uk-margin-xsmall-right"></span>
								<span>Filter<span v-if="state.filterCount > 0">&nbsp;({{ state.filterCount }})</span></span>
							</a>
						</li>
					</ul>
					<ul ref="switcherContent" class="uk-switcher uk-position-relative">

						<li :class="{ 'uk-active': state.switcherIsActive === true && state.switcherIndex === 0 }">
							<div class="uk-text-default uk-text-semibold uk-text-primary">Søk på sted eller adresse</div>
							<LocationSearcher />
							<a uk-close @click="closeSwitcher"></a>
						</li>
						<li :class="{ 'uk-active': state.switcherIsActive === true && state.switcherIndex === 1 }">
							<MapFilter @filter="filterResults" />
							<a uk-close @click="closeSwitcher"></a>
						</li>
					</ul>
				</div>
			</div>
			<div class="uk-position-relative">
				<Loader v-if="isLoading" class="uk-position-center" />
				<div v-else>
					<div class="uk-margin-bottom uk-margin-top uk-text-default uk-visible@m">
						<div>{{ results.length }} strekninger innenfor søkeområdet</div>
						<div class="uk-text-small">Sortert fra nord til sør</div>
					</div>
					<SearchResults @hoverItem="hoverMarker" :searchResults="results" class="uk-visible@m" />
				</div>
			</div>

		</div>

		<div class="uk-width-1-1@m uk-width-1-1@l uk-height-viewport uk-background-white">
			<div id="map-container" class="uk-position-cover  uk-width-1-1"></div>
		</div>

		<Drawer :close="state.switcherIsActive" class="uk-hidden@m uk-width-1-1">

			<template #header v-if="!isLoading">
				<div class="uk-text-center uk-margin-bottom">
					<div>{{ results.length }} strekninger innenfor søkeområdet</div>
					<div class="uk-text-small">Sortert fra nord til sør</div>
				</div>
			</template>

			<SearchResults @hoverItem="hoverMarker"
				gridClass="uk-child-width-1-1 uk-padding-small uk-padding-remove-horizontal" :searchResults="results" />

			<Loader v-if="isLoading" />
		</Drawer>


	</div>
</template>

<script setup lang="ts">
import { Ref, onMounted, onBeforeUnmount, reactive, watch, ref, provide, inject } from 'vue'
import { debounce } from 'lodash'
import * as L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet-geometryutil'
import { FeatureLayer } from 'esri-leaflet'
import * as Vector from 'esri-leaflet-vector'
import { GestureHandling } from "leaflet-gesture-handling";
import MapFilter from './MapFilter.vue'
import SearchResults from './SearchResults.vue'
import { ISearchResult } from '@/interfaces/SearchResult'
import { IFeatureLayerOptions } from '@/interfaces/FeatureLayerOptions'
import { WebMapDetails } from '@/interfaces/WebMapDetails';
import Drawer from './Drawer.vue'
import LocationSearcher from './LocationSearcher.vue'
import { CustomMarker } from './CustomMarker.ts'
import Loader from './Loader.vue'
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import { CustomMarkerOptions } from '@/interfaces/MarkerOptions'

const state = reactive({
	map: null as any,
	initiated: false as boolean,
	webMapDetails: {} as Record<string, WebMapDetails>,
	switcherIsActive: false as boolean,
	switcherIndex: 0 as number,
	closeDrawer: false as boolean,
	baseLayer: null as L.TileLayer | null,
	combinedBounds: null as L.LatLngBounds | null,
	layerCounter: 0 as number,
	totalLayerCount: 0 as number,
	featureLayerInstances: [] as FeatureLayer[],
	visibleLayers: [] as FeatureLayer[],
	layersAreLoaded: false as boolean,
	filterCount: 0 as number,
	xsrfToken: document.querySelector('input[name="__RequestVerificationToken"]')?.getAttribute('value'),
	markers: L.markerClusterGroup({
		removeOutsideVisibleBounds: true,
		showCoverageOnHover: false,
		maxClusterRadius: function (zoom: number) {
			return (zoom >= 10 ? 1 : 35);
		}
	}),
	// markers: [] as L.Marker[],
	markerData: [] as any[]
})
const switcherNav: Ref<HTMLElement | null> = ref(null)

const switcherContent: Ref<HTMLElement | null> = ref(null)

const featureLayers = ref([] as L.esri.FeatureLayer[])

const mapBounds = ref(null as L.LatLngBounds | null)

const results = ref<ISearchResult[]>([])

const mapCenter = ref<L.LatLng | null>(null);

let pauseBeforeQuery: ReturnType<typeof setTimeout>

const isLoading = ref(true)

const filterValue = ref<string[]>([]);

const config = inject('config') as any | null
const token = inject('token') as Ref<string> | null

provide('mapCenter', mapCenter)

provide('setMapCenter', (newCenter: L.LatLng) => {

	if (state.map) {
		state.map.setView([newCenter.lat, newCenter.lng], 10, {
			pan: {
				animate: true,
				duration: 1.5
			},
			zoom: {
				animate: true
			}
		});
	}
});



onMounted(async () => {
	init()
	document.addEventListener('click', checkCloseSwitcher, false);
	document.addEventListener('touchstart', checkCloseSwitcher, false);

})

const checkCloseSwitcher = (event: MouseEvent | TouchEvent) => {
	const target = event.target as HTMLElement

	if (window.innerWidth >= 1024) return;

	if (switcherNav.value && !switcherNav.value.contains(target)
		&& switcherContent.value && !switcherContent.value.contains(target)) {
		closeSwitcher();
	}
};

onBeforeUnmount(() => {
	document.removeEventListener('click', checkCloseSwitcher, false);
	document.removeEventListener('touchstart', checkCloseSwitcher, false);
});

function hoverMarker(result: ISearchResult | null) {

	const hoverName = result != null ? result.name : null

	state.markers.eachLayer((layer: L.Layer) => {
		if (hoverName == null) {
			layer.fireEvent('mouseout');
		} else {
			const markerName = (layer.options as any).name;
			if (markerName == null) return
			if (markerName !== hoverName) {
				layer.fireEvent('mouseout');
			} else {
				layer.fireEvent('mouseenter');
			}
		}
	})
}

function setActiveSwitcherState(index: number) {

	state.switcherIsActive = true
	if (!state.switcherIsActive) return
	state.switcherIndex = index
}

function closeSwitcher() {
	switcherNav.value?.querySelector('.uk-active')?.classList.remove('uk-active')
	switcherContent.value?.querySelector('.uk-active')?.classList.remove('uk-active')

	state.switcherIsActive = false
}

function filterResults(filter: string[]) {
	filterValue.value = filter;
	state.filterCount = filter.length
	filterMarkers(filterValue.value)
}
function filterMarkers(filter: string[]) {

	state.markers.clearLayers()

	if (!results.value || !results.value.length) return // no need to add any markers if there are no results
	//const showProjects = results.value.map(result => result.arcgisProjectName).join("','")

	state.markerData.forEach((marker: L.Marker) => {
		const options = marker.options as CustomMarkerOptions;
		if (!filter.length || (options.status && filter.includes(options.status))) {
			state.markers.addLayer(marker);
		}
	})

}

function getTranslatedStatus(status: string) {
	switch (status) {
		case 'Forberedes':
			return 'Preparing';
		case 'Planlegges':
			return 'Planning';
		case 'Building':
			return 'Bygger';
		case 'Kontrakt':
			return 'Building';
		case 'Åpnet':
			return 'Completed';
		case 'Bygger':
			return 'Building';
		default:
			return 'Planning';
	}

}
function getTranslatedStatuses(statuses: string[]) {
	return statuses.map(status => getTranslatedStatus(status))
}

watch(results, async (newResults) => {
	if (newResults != null) {
		// state.markers.clearLayers()
		if (featureLayers.value.length) return

		const featureLayerData = await getMapFeatureLayers('https://services-eu1.arcgis.com/Zxn04POL7OfqZkir/arcgis/rest/services/NV_SenterlinjePortefolje_Niv0/FeatureServer/0')
		featureLayers.value.push(featureLayerData.layer)
	}
})

watch(filterValue, async () => {
	results.value = await searchUmbraco(state.map.getBounds())
})

watch(mapBounds, async (newBounds) => {
	if (newBounds != null) {
		if (pauseBeforeQuery) clearTimeout(pauseBeforeQuery)
		pauseBeforeQuery = setTimeout(async () => { // Wait before performing query in case the user needs to navigate more before the query is performed

			results.value = await searchUmbraco(newBounds)
		}, 700);
	}
})

async function init() {

	L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

	let initialZoom = 4
	if (window.innerWidth >= 640) initialZoom = 5
	if (window.innerWidth >= 1440) initialZoom = 6

	state.map = L.map('map-container', {
		zoomControl: false,
		minZoom: 4,
		maxZoom: 13,
		attributionControl: false,
		gestureHandling: true
	}).setView([64.847784249723, 13.666992187500002], initialZoom)


	const basemapUrl = config?.basemapUrl

	if (token) {
		watch(token, (newToken) => {
			if (newToken) {
				setBaseMapLayer(basemapUrl)
			}
		})
	}

	addMapControls()

	mapBounds.value = state.map.getBounds()

	state.map.on('moveend', debounce(async () => {
		// state.map.closePopup();
		mapBounds.value = state.map.getBounds()
		mapCenter.value = state.map.getCenter()
	}, 700))

	function addMapControls() {
		// Add Zoom control
		L.control.zoom({
			position: 'bottomright'
		}).addTo(state.map);
	}

}

async function searchUmbraco(bounds: L.LatLngBounds): Promise<ISearchResult[]> {

	if (!state.xsrfToken) throw new Error('Invalid request') // missing token

	isLoading.value = true



	let parameters = {};

	if (!state.initiated) {
		parameters = {
			StatusFilter: getTranslatedStatuses(filterValue.value)
		}
		state.initiated = true

	} else {
		const _northEast = bounds.getNorthEast();
		const _southWest = bounds.getSouthWest();
		parameters = {
			North: _northEast.lat,
			East: _northEast.lng,
			South: _southWest.lat,
			West: _southWest.lng,
			StatusFilter: getTranslatedStatuses(filterValue.value)
		}
	}
	try {
		const response = await fetch('/umbraco/surface/searchumbraco/getprojects', {
			method: 'POST',
			body: JSON.stringify(parameters),
			headers: {
				'Content-Type': 'application/json',
				'RequestVerificationToken': state.xsrfToken,
			},
		});
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return await response.json();
	} finally {
		isLoading.value = false;
	}
}

function setBaseMapLayer(url: string) {
	try {
		if (state.map) {
			if (state.baseLayer && state.map.hasLayer(state.baseLayer)) {
				state.map.removeLayer(state.baseLayer); // Remove the current base layer if it exists
			}
			// Check if the URL is a Vector Tile Server URL
			if (url.includes('VectorTileServer')) {
				// Add a vector tile layer
				state.baseLayer = Vector.vectorTileLayer(url, {}).addTo(state.map);
			} else {
				// Add a raster tile layer
				state.baseLayer = L.tileLayer(url + '/tile/{z}/{y}/{x}').addTo(state.map);
				//getMapLayers(url.split('id=')[1])
			}
		}
	} catch (error) {
		console.error(error);
	}
}
function getFeatureColor(feature: any) {

	const status = results.value.find(item => item.arcgisProjectName === feature.properties?.parsell)?.projectStatus
	let color;
	if (status === 'Building') {
		color = '#F58500';
	} else if (status == 'Preparing') {
		color = '#FF5BD1';
	} else if (status == 'Planning') {
		color = '#EF281F';
	} else if (status == 'Completed') {
		color = '#006210';
	} else {
		color = '#F13F37';
	}
	return color;
}

async function getMapFeatureLayers(url: string): Promise<{ layer: L.esri.FeatureLayer }> {
	return new Promise((resolve, reject) => {
		const featureOptions: IFeatureLayerOptions = {
			url: url,
			apiKey: import.meta.env.VITE_APP_ARCGIS_API_KEY,
			simplifyFactor: 0.35,
			precision: 5,
			style: (feature: any) => {
				return { color: getFeatureColor(feature), weight: 5 };
			},
			// Set interactive to false to disable click events on the feature layers
			interactive: false,
			bubblingMouseEvents: false
		};

		if (!results.value || !results.value.length) {
			return reject({ message: 'No results available' });
		}

		// Separate results into mainProject and roadStretch
		const mainProjectResults = results.value.filter(result => result.docTypeAlias === 'mainProject');
		const roadStretchResults = results.value.filter(result => result.docTypeAlias === 'roadStretch');

		// Function to add layers based on filtered results
		const addLayers = (filteredResults: any[]) => {
			filteredResults.forEach(result => {
				const featureLayerOptions = { ...featureOptions, where: `parsell = '${result.arcgisProjectName}'` };
				const featureLayer = new FeatureLayer(featureLayerOptions).addTo(state.map);

				featureLayer.on('createfeature', (event: any) => {
					if (!event.feature.properties.parsell) return;
					const coordinates = event.feature.geometry.coordinates;
					const polylinePoints: any = [];

					if (event.feature.geometry.type === 'LineString') {
						coordinates.forEach((coord: any) => {
							const [lng, lat] = coord;
							polylinePoints.push([lat, lng]);
						});
					} else if (event.feature.geometry.type === 'MultiPolygon') {
						coordinates.forEach((polygon: any) => {
							polygon.forEach((ring: any) => {
								ring.forEach((coord: any) => {
									const [lng, lat] = coord;
									polylinePoints.push([lat, lng]);
								});
							});
						});
					} else {
						coordinates.forEach((line: any) => {
							line.forEach((point: any) => {
								const [lng, lat] = point;
								polylinePoints.push([lat, lng]);
							});
						});
					}

					const middlePoint = polylinePoints[Math.floor(polylinePoints.length / 2)];

					if (middlePoint[0] < -90 || middlePoint[0] > 90 || middlePoint[1] < -180 || middlePoint[1] > 180) {
						console.error('Invalid middlePoint:', middlePoint);
						return;
					}

					const markerData = results.value.find(result => result.arcgisProjectName === event.feature.properties.parsell);
					let marker = null;

					if (markerData != null) {
						marker = CustomMarker(middlePoint, event.feature.properties, markerData);
					} else {
						marker = CustomMarker(middlePoint, event.feature.properties);
					}

					try {
						if (results.value.length) {
							if (markerData != null) {
								state.markers.addLayer(marker);
								state.markerData.push(marker);
							}
						}
					} catch (error) {
						console.error(error);
					}
				});

				// Optional: You can still keep the mouseover for visual feedback without making it clickable
				featureLayer.on('mouseover', (e: any) => {
					const layer = e.layer || e.target;
					if (layer && layer.setStyle) {
						layer.setStyle({ opacity: 0.7 }); // Just a visual effect on hover
					}
				});

				featureLayer.on('mouseout', (e: any) => {
					const layer = e.layer || e.target;
					if (layer && layer.setStyle) {
						layer.setStyle({ opacity: 1 });
					}
				});

				state.markers.addTo(state.map);
			});
		};

		// Add mainProject layers first
		addLayers(mainProjectResults);

		// Add roadStretch layers next
		addLayers(roadStretchResults);
		resolve({ layer: state.map });
	});
}

</script>

<style>
.uk-background-white {
	background-color: #FFF;
}

.leaflet-bar.leaflet-control-zoom>a:first-child {
	border-top-right-radius: 8px;
	border-top-left-radius: 8px;
}

.leaflet-bar.leaflet-control-zoom>a:last-child {
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
}

.leaflet-bar.leaflet-control-zoom>a {
	background-color: #285960;
	color: #fff;
	padding: 3px;
	font-size: 22px;
	width: 30px;
	height: 30px;
	line-height: 30px;
}

.leaflet-bar.leaflet-control-zoom {
	border: none;
}

.leaflet-bar.leaflet-control-zoom>a:focus,
.leaflet-bar.leaflet-control-zoom>a:hover {
	background-color: #d6f19b;
	color: #285960;
}

@media (min-width: 680px) {
	.leaflet-bar.leaflet-control-zoom>a {
		padding: 7px;
	}
}

.switcher-container {
	width: 100%;
}

.panel-toggler {
	position: absolute;
	right: 0;
	background-color: #FAFAFA;
	color: #181818;
	padding: 8px;
	border-radius: 8px;
	transition: transform .2s ease-in;
	transform: rotate(0deg);
	overflow: hidden;

	>span:not([uk-icon]) {
		display: none;
	}

	&:focus,
	&:hover {
		color: #181818;
	}
}

#results-panel {
	background-color: #FAFAFA;
	transition: transform .5s ease-in-out;
	z-index: 2;
	/*transform: translateX(0);
  transition: transform .5s ease-in-out;*/
	border-bottom-right-radius: 32px;
}

#results-panel.hide {
	transform: translateX(-100%);
	transition: transform .5s ease-in-out;

	.panel-toggler {
		padding: 16px;

		>[uk-icon] {
			transition: transform .2s ease-in;
			transform: rotate(180deg);
		}

		>span:not([uk-icon]) {
			display: inline-block;
		}
	}
}

#results-panel>.uk-sticky.uk-active {
	box-sizing: content-box;
	margin-left: -32px !important;
	margin-right: -32px !important;
	padding-left: 32px;
	padding-right: 32px;
	background-color: #FAFAFA;
	border-bottom: 1px #D9D9D9 solid;
	box-shadow: -2px 4px 3px rgba(16, 24, 40, 0.10);
}

#switcher-nav {
	margin-bottom: 8px;
	text-align: center;
	z-index: 2;
	list-style: none;
	padding-left: 0;

	>li {
		display: inline-block;
	}

	>li>a {
		border: 2px solid #285960;
		line-height: 24px;
		padding: 8px 16px;
	}

	>li:first-child>a {
		border-bottom-left-radius: 8px;
		border-top-left-radius: 8px;
		border-bottom-right-radius: 0;
		border-top-right-radius: 0;
	}

	>li:last-child>a {
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		border-top-right-radius: 8px;
		border-bottom-right-radius: 8px;
	}

	>.uk-active>a {
		background-color: #D6F19B;
		color: #285960;
	}
}

.uk-switcher {
	width: 100%;
	max-width: 320px;
	margin: 0 auto;
	background: #FAFAFA;
	border-radius: 16px;
	margin-bottom: 20px;
	box-shadow: 0px 4px 8px -2px rgba(16, 24, 40, 0.10);

	>li:first-child {
		padding-top: 24px;
	}

	>li {
		padding: 24px;

		[uk-close] {
			position: absolute;
			top: 8px;
			right: 8px;
			color: #285960;
		}
	}
}

#map-wrapper {
	position: relative;
	z-index: 1;
}

#map-container {
	height: 80vh;
	width: 100%;
	max-width: 100%;
	position: relative;
	z-index: 1;
	background-color: #FFF;
}

.custom-marker {
	transform-style: flat !important;
}

.custom-marker.hover .icon-container,
.custom-marker:hover .icon-container,
.custom-marker:focus .icon-container {
	background-color: #D6F19B;
	color: #285960;
}

.custom-marker .icon-container.mainProject {
	background-color: #FFE5FA;
	color: #285960;
}

.custom-marker .icon-container.mainProject:before {
	background-color: #FFE5FA;
}

.custom-marker .icon-container {
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: #285960;
	color: #FFF;
	border-radius: 4px;
	border: 1px solid #285960;
}

.icon-container:before {
	content: '';
	display: block;
	position: absolute;
	top: 100%;
	left: 50%;
	width: 10px;
	height: 10px;
	background-color: #285960;
	border-right: 1px solid #285960;
	border-bottom: 1px solid #285960;
	transform: translate(-50%, -5px) rotate(45deg);
}

.custom-marker.hover .icon-container:before,
.icon-container:hover:before {
	background-color: #D6F19B;
}

.custom-popup {
	font-size: 14px;
	transition: none !important;
	max-width: 224px;

	.leaflet-popup-content {
		max-width: 184px;

		p {
			margin: 8px 0 0 0;
		}

		.uk-icon {
			transform: translateX(0);
			transition: transform ease-in-out 200ms;
		}

		a:hover .uk-icon {
			transform: translateX(10px);
			transition: transform ease-in-out 200ms;
		}
	}
}

.custom-popup .leaflet-popup-content-wrapper {
	border-top-right-radius: 0;
	border-bottom-left-radius: 0;
}

.custom-popup.mainProject {

	a.leaflet-popup-close-button,
	a.leaflet-popup-close-button:hover,
	a.leaflet-popup-close-button:focus {
		color: #285960;
		text-decoration: none;
	}

	.leaflet-popup-content-wrapper {
		background-color: #FFE5FA;
		border: 1px solid #285960;
		color: #285960;

		a {
			color: #285960;
		}
	}

}

.custom-popup.mainProject .leaflet-popup-tip {
	background-color: #FFE5FA;
	border: 1px solid #285960;
}

.custom-popup .leaflet-popup-content-wrapper,
.custom-popup .leaflet-popup-tip {
	background-color: #285960;
	color: #FFF;
}

a.leaflet-popup-close-button {
	position: absolute;
	right: 5px;
	top: 5px;
}

.custom-popup a.leaflet-popup-close-button,
.custom-popup a.leaflet-popup-close-button:hover {
	color: #FFF;
}

.custom-popup a.leaflet-popup-close-button>span {
	font-size: 0;
}

.custom-popup a {
	color: #FAFAFA;
	text-decoration: none;
	font-size: 1rem;
	display: inline-block;
	font-weight: 600;
}

.custom-popup a:not(.leaflet-popup-close-button):after {
	content: '';
	width: 100%;
	height: 1px;
	margin-top: 2px;
	background-color: transparent;
	display: block;
}

.custom-popup a:hover:after,
.custom-popup a:focus:after {
	background-color: #FAFAFA;
}

.custom-popup a .uk-icon {
	width: 16px;
	height: 16px;
}

.marker-cluster {
	border-radius: 50%;
	background-color: #285960;
	border: 1px solid #285960;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #FFF;
	transition: all .2s ease-in-out;
}

.marker-cluster:hover {
	background-color: #D6F19B;
	color: #285960;
	opacity: 1;
}

@media(min-width: 680px) {
	#switcher-nav>li>a {
		padding: 12px 20px;
	}

	.custom-popup a {
		font-size: 1.125rem;
	}

	.custom-popup a .uk-icon {
		width: 20px;
		height: 20px;
	}

	.leaflet-popup-content {
		margin: 0;
		padding: 20px 20px 24px 20px;
	}
}

@media(min-width: 1024px) {
	#results-panel {
		padding-left: 48px;
		padding-right: 48px;

		.panel-toggler {
			right: -108px;
		}

		.uk-sticky.uk-active {
			margin-left: -48px !important;
			margin-right: -48px !important;
			padding-left: 48px;
			padding-right: 48px;

			.panel-toggler {
				right: -60px;
				top: 24px;
			}
		}
	}

	#results-panel.hide {
		.panel-toggler {
			right: -191px;
		}

		.uk-sticky.uk-active {
			.panel-toggler {
				right: -143px;
				top: 24px;
			}
		}
	}

	.switcher-container {
		z-index: 2;
		position: static !important;
		transform: translateX(0) !important;
	}

	#switcher-nav {
		text-align: initial;
	}

	.uk-switcher {
		max-width: calc(100% - 32px);
		margin: 0;
		box-shadow: none;

		.uk-close {
			display: none;
		}

		>li {
			padding-left: 0;
			padding-right: 0;
			padding-bottom: 0;
		}
	}

	#map-wrapper {
		min-height: 100vh;

		.uk-text-default {
			font-size: 1.125rem;
			line-height: 1.6875rem;
		}

		.uk-label {
			min-width: 110px;
			text-align: center;
		}
	}

	#map-container {
		height: 100vh;
		position: fixed;
	}
}

@media(min-width: 1440px) {
	#results-panel {
		padding-left: 56px;
		padding-right: 56px;

		.uk-sticky.uk-active {
			margin-left: -56px !important;
			margin-right: -56px !important;
			padding-left: 56px;
			padding-right: 56px;

			.panel-toggler {
				right: -68px;
			}
		}

		.panel-toggler {
			right: -124px;
		}
	}

	#results-panel.hide {
		.uk-sticky.uk-active {
			.panel-toggler {
				right: -151px;
				top: 24px;
			}
		}

		.panel-toggler {
			right: -207px;
		}
	}
}
</style>
